import axios from '@/axios';
import { useBookingStore } from '@/stores/booking';
import type { ServiceVariation, ServiceVariationGroupCategory } from '@/types';
import widgetSettings from '@/widget-settings';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, ref } from 'vue';

const flattenVariations = (categories: ServiceVariationGroupCategory[]) => {
  const result: ServiceVariation[] = [];
  categories.forEach((category) => {
    category.service_variation_groups.forEach((serviceVariationGroup) => {
      serviceVariationGroup.variations.forEach((variation) => {
        result.push(variation);
      });
    });
  });
  return result;
};

export const useServicesStore = defineStore('servicesVariationGroups', () => {
  const categories = ref<ServiceVariationGroupCategory[]>([]);
  const variations = computed<ServiceVariation[]>(() =>
    flattenVariations(categories.value)
  );
  const loading = ref(false);

  const fetchServices = () => {
    const { locationId, employeeId } = useBookingStore();
    loading.value = true;

    axios
      .get(`/locations/${locationId}/service_variation_groups`, {
        params: {
          resource_id: employeeId,
          widget_id: widgetSettings.widgetId
        }
      })
      .then(({ data: { service_categories } }) => {
        categories.value = service_categories;

        if (widgetSettings.services.length) {
          const bookingsStore = useBookingStore();
          widgetSettings.services.forEach((serviceId) => {
            if (
              variations.value.find(
                (service) => service.id === Number.parseInt(serviceId)
              )
            ) {
              bookingsStore.serviceIds.push(Number.parseInt(serviceId));
            }
          });
        }
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const filterByNameOrDescription = (
    item: { name: string; description?: string },
    searchQuery: string
  ) =>
    !!(
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const filterServiceCategories = (
    categories: ServiceVariationGroupCategory[],
    searchQuery: string
  ) =>
    categories
      .map((category) => ({
        ...category,
        service_variation_groups: category.service_variation_groups
          .map((group) => ({
            ...group,
            variations: group.variations.filter((variation) =>
              filterByNameOrDescription(variation, searchQuery)
            )
          }))
          .filter(
            (group) =>
              group.variations.length ||
              filterByNameOrDescription(group, searchQuery)
          )
      }))
      .filter((category) => category.service_variation_groups.length);

  const serviceById = (id: number) =>
    variations.value.find((variation) => variation.id === id);

  return {
    categories,
    services: variations,
    loading,
    filterServiceCategories,
    fetchServices,
    serviceById
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useServicesStore, import.meta.hot));
}
